import { DatePicker, Form, Input, Select } from "antd";

import {
  disableTodayAndPast,
  type CompanyOption,
} from "../model/proposeEditor";
import type { ProposeRecipient } from "../model/types";

type ProposeBasicInfoFieldsProps = {
  variant: "desktop" | "mobile";
  customers: CompanyOption[];
  recipients: ProposeRecipient[];
  fieldSet?: "all" | "coordinator-basic" | "coordinator-schedule";
};

const recipientOptions = (recipients: ProposeRecipient[]) =>
  recipients.map((recipient) => ({
    value: recipient.user_id,
    label: recipient.display_name || recipient.email,
  }));

const BasicFields = ({
  customers,
  recipients,
  mobile,
  fieldSet = "all",
}: Omit<ProposeBasicInfoFieldsProps, "variant"> & { mobile: boolean }) => {
  const { RangePicker } = DatePicker;
  const selectedRepresentativeId = Form.useWatch("representative_id");
  const selectedCompany = customers.find(
    (customer) => customer.value === selectedRepresentativeId,
  );
  const buyerCodeField = (
    <div className="propose-company-field">
        <Form.Item
          name="representative_id"
          label={mobile ? "会社" : "買受人コード"}
          rules={[{ required: true, message: mobile ? "会社を選択してください" : "買受人コードを選択してください" }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            options={customers}
            placeholder={mobile ? "会社を選択" : "買受人コードを選択"}
          />
        </Form.Item>
        {selectedCompany && (
          <div className="propose-selected-company-name" aria-live="polite">
            <span>会社名</span>
            <strong>{selectedCompany.name}</strong>
          </div>
        )}
    </div>
  );
  const recipientField = (
    <Form.Item
        name="recipient_id"
        label="買受人担当者名"
        rules={[{ required: true, message: "買受人担当者名を選択してください" }]}
      >
        <Select
          allowClear={!mobile}
          options={recipientOptions(recipients)}
          placeholder="買受人担当者名を選択"
        />
    </Form.Item>
  );
  const deliveryDateField = (
    <Form.Item
        name="estimated_delivery_date"
        label={mobile ? "出荷日" : "納品予定日"}
        rules={[{ required: true, message: `${mobile ? "出荷日" : "納品予定日"}を選択してください` }]}
      >
        <DatePicker format="YYYY/MM/DD" style={mobile ? undefined : { width: "100%" }} />
    </Form.Item>
  );
  const effectivePeriodField = (
    <Form.Item
        name="effective_period"
        label="提案有効期間"
        rules={[{ required: true, message: "有効期間を選択してください" }]}
      >
        <RangePicker
          className={mobile ? "propose-mobile-range-picker" : undefined}
          popupClassName={mobile ? "propose-mobile-range-picker-dropdown" : undefined}
          disabledDate={disableTodayAndPast}
          format="YYYY/MM/DD"
          inputReadOnly={mobile}
          style={mobile ? undefined : { width: "100%" }}
        />
    </Form.Item>
  );
  const receiptDateField = (
    <Form.Item
        name="estimated_receipt_date"
        label={mobile ? "店着予定日" : "受取予定日"}
        rules={[{ required: true, message: `${mobile ? "店着予定日" : "受取予定日"}を選択してください` }]}
      >
        <DatePicker format="YYYY/MM/DD" style={mobile ? undefined : { width: "100%" }} />
    </Form.Item>
  );
  const salesDeadlineField = (
    <Form.Item
        name="expires_at"
        label="営業入力期限"
        rules={[{ required: true, message: "営業入力期限を選択してください" }]}
      >
        <DatePicker
          showTime
          format="YYYY/MM/DD HH:mm"
          inputReadOnly={mobile}
          needConfirm={mobile ? false : undefined}
          popupClassName={mobile ? "propose-mobile-deadline-picker-dropdown" : undefined}
          style={mobile ? undefined : { width: "100%" }}
        />
    </Form.Item>
  );
  const noteField = (
    <Form.Item name="note" label="備考">
        <Input.TextArea rows={2} placeholder={mobile ? "備考を入力（任意）" : "備考を入力"} />
    </Form.Item>
  );
  const orderNameField = (
    <Form.Item
      name="order_name"
      label="提案書名"
      rules={[{ required: true, message: "提案書名を入力してください" }]}
    >
      <Input placeholder="提案書名" />
    </Form.Item>
  );

  if (fieldSet === "coordinator-basic") {
    return <>{orderNameField}{buyerCodeField}{recipientField}{salesDeadlineField}</>;
  }
  if (fieldSet === "coordinator-schedule") {
    return <>{deliveryDateField}{effectivePeriodField}{receiptDateField}</>;
  }
  return <>{buyerCodeField}{recipientField}{deliveryDateField}{effectivePeriodField}{receiptDateField}{salesDeadlineField}{noteField}</>;
};

export const ProposeBasicInfoFields = (props: ProposeBasicInfoFieldsProps) => {
  if (props.variant === "mobile")
    return <BasicFields {...props} mobile />;

  const layoutClassName =
    props.fieldSet === "coordinator-basic"
      ? "propose-basic-info-desktop propose-basic-info-desktop--coordinator-basic"
      : props.fieldSet === "coordinator-schedule"
        ? "propose-basic-info-desktop propose-basic-info-desktop--coordinator-schedule"
        : "propose-basic-info-desktop";

  return <div className={layoutClassName}><BasicFields {...props} mobile={false} /></div>;
};
